import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Breadcrumbs,
  Link,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  gitlabApi,
  GitLabInstance,
  GitLabProject,
  GitLabBranch,
  GitLabTreeItem,
} from '../services/gitlab-api';

interface TerraformCommitDialogProps {
  open: boolean;
  onClose: () => void;
  hcl: string;
  defaultFilename?: string;
}

export const TerraformCommitDialog: React.FC<TerraformCommitDialogProps> = ({
  open,
  onClose,
  hcl,
  defaultFilename = 'main.tf',
}) => {
  const [instances, setInstances] = useState<GitLabInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [branches, setBranches] = useState<GitLabBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [newBranchName, setNewBranchName] = useState('');
  const [createNewBranch, setCreateNewBranch] = useState(false);
  const [filePath, setFilePath] = useState(defaultFilename);
  const [commitMessage, setCommitMessage] = useState('Add terraform configuration');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // File picker state
  const [showBrowser, setShowBrowser] = useState(false);
  const [treeItems, setTreeItems] = useState<GitLabTreeItem[]>([]);
  const [treePath, setTreePath] = useState('');
  const [treeLoading, setTreeLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadInstances();
      setError(null);
      setSuccess(false);
      setShowBrowser(false);
    }
  }, [open]);

  useEffect(() => {
    if (selectedInstance) loadProjects();
  }, [selectedInstance]);

  useEffect(() => {
    if (selectedInstance && selectedProject) loadBranches();
  }, [selectedInstance, selectedProject]);

  const loadInstances = async () => {
    try {
      const data = await gitlabApi.listInstances();
      setInstances(data);
      if (data.length === 1) setSelectedInstance(data[0].id);
    } catch (err: any) {
      setError('Failed to load GitLab instances');
    }
  };

  const loadProjects = async () => {
    try {
      const data = await gitlabApi.listProjects(selectedInstance);
      setProjects(data);
    } catch (err: any) {
      setError('Failed to load projects');
    }
  };

  const loadBranches = async () => {
    try {
      const data = await gitlabApi.listBranches(selectedInstance, selectedProject as number);
      setBranches(data);
      const defaultBranch = data.find(b => b.default);
      if (defaultBranch) setSelectedBranch(defaultBranch.name);
    } catch (err: any) {
      setError('Failed to load branches');
    }
  };

  // File tree browsing
  const browseTree = async (path: string = '') => {
    if (!selectedInstance || !selectedProject) return;
    setTreeLoading(true);
    try {
      const items = await gitlabApi.browseTree(
        selectedInstance,
        selectedProject as number,
        path || undefined,
        selectedBranch || undefined,
      );
      setTreeItems(items);
      setTreePath(path);
    } catch {
      setTreeItems([]);
    } finally {
      setTreeLoading(false);
    }
  };

  const openBrowser = () => {
    setShowBrowser(true);
    // Start browsing from current filePath's directory
    const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
    browseTree(dir);
  };

  const handleTreeItemClick = (item: GitLabTreeItem) => {
    if (item.type === 'tree') {
      browseTree(item.path);
    } else {
      // Select this file path
      setFilePath(item.path);
      setShowBrowser(false);
    }
  };

  const handleSelectDirectory = () => {
    // Use the current browsed directory + keep current filename
    const currentFilename = filePath.includes('/') ? filePath.substring(filePath.lastIndexOf('/') + 1) : filePath;
    const newPath = treePath ? `${treePath}/${currentFilename}` : currentFilename;
    setFilePath(newPath);
    setShowBrowser(false);
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      browseTree('');
    } else {
      const parts = treePath.split('/');
      const path = parts.slice(0, index + 1).join('/');
      browseTree(path);
    }
  };

  const handleCommit = async () => {
    if (!selectedInstance || !selectedProject || !filePath || !commitMessage) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let targetBranch = selectedBranch;

      if (createNewBranch && newBranchName) {
        await gitlabApi.createBranch(selectedInstance, selectedProject as number, newBranchName, selectedBranch);
        targetBranch = newBranchName;
      }

      // Try create first, fall back to update
      try {
        await gitlabApi.createOrUpdateFile(
          selectedInstance,
          selectedProject as number,
          filePath,
          hcl,
          targetBranch,
          commitMessage,
          'create',
        );
      } catch {
        await gitlabApi.createOrUpdateFile(
          selectedInstance,
          selectedProject as number,
          filePath,
          hcl,
          targetBranch,
          commitMessage,
          'update',
        );
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Commit to GitLab</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>File committed successfully!</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>GitLab Instance</InputLabel>
            <Select
              value={selectedInstance}
              label="GitLab Instance"
              onChange={e => {
                setSelectedInstance(e.target.value);
                setSelectedProject('');
                setBranches([]);
                setShowBrowser(false);
              }}
            >
              {instances.map(inst => (
                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProject}
              label="Project"
              onChange={e => { setSelectedProject(e.target.value as number); setShowBrowser(false); }}
              disabled={!selectedInstance}
            >
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name_with_namespace}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Branch</InputLabel>
            <Select
              value={selectedBranch}
              label="Branch"
              onChange={e => { setSelectedBranch(e.target.value); setShowBrowser(false); }}
              disabled={!selectedProject}
            >
              {branches.map(b => (
                <MenuItem key={b.name} value={b.name}>
                  {b.name}{b.default ? ' (default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant={createNewBranch ? 'contained' : 'outlined'}
              onClick={() => setCreateNewBranch(!createNewBranch)}
            >
              New Branch
            </Button>
            {createNewBranch && (
              <TextField
                size="small"
                value={newBranchName}
                onChange={e => setNewBranchName(e.target.value)}
                placeholder="branch-name"
                sx={{ flex: 1 }}
              />
            )}
          </Box>

          {/* File path with browse button */}
          <TextField
            fullWidth
            size="small"
            label="File Path"
            value={filePath}
            onChange={e => setFilePath(e.target.value)}
            placeholder="scenarios/my-scenario/main.tf"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={openBrowser}
                    disabled={!selectedInstance || !selectedProject}
                    title="Browse repository"
                  >
                    <FolderOpenIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Inline file browser */}
          {showBrowser && (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              {/* Breadcrumb navigation */}
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Breadcrumbs separator="/" sx={{ flex: 1, '& .MuiBreadcrumbs-separator': { mx: 0.25 } }}>
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    color={treePath ? 'primary' : 'text.primary'}
                    fontWeight={treePath ? 400 : 600}
                    onClick={() => navigateToBreadcrumb(-1)}
                    sx={{ fontFamily: 'monospace' }}
                  >
                    /
                  </Link>
                  {treePath.split('/').filter(Boolean).map((segment, i) => {
                    const isLast = i === treePath.split('/').filter(Boolean).length - 1;
                    return (
                      <Link
                        key={i}
                        component="button"
                        variant="caption"
                        underline="hover"
                        color={isLast ? 'text.primary' : 'primary'}
                        fontWeight={isLast ? 600 : 400}
                        onClick={() => navigateToBreadcrumb(i)}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {segment}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
                <Button size="small" variant="text" onClick={handleSelectDirectory} sx={{ minWidth: 'auto', fontSize: '0.7rem' }}>
                  Use this folder
                </Button>
              </Box>

              {/* Tree items */}
              <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                {treeLoading ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                ) : treeItems.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Empty directory</Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {/* Directories first, then files */}
                    {[...treeItems].sort((a, b) => {
                      if (a.type === b.type) return a.name.localeCompare(b.name);
                      return a.type === 'tree' ? -1 : 1;
                    }).map(item => (
                      <ListItem
                        key={item.path}
                        component="div"
                        sx={{
                          cursor: 'pointer',
                          py: 0.25,
                          px: 1.5,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => handleTreeItemClick(item)}
                      >
                        {item.type === 'tree' ? (
                          <FolderIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                        ) : (
                          <InsertDriveFileIcon fontSize="small" sx={{ mr: 1, color: item.name.endsWith('.tf') ? 'primary.main' : 'action.active' }} />
                        )}
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            fontWeight: item.name.endsWith('.tf') ? 600 : 400,
                            color: item.name.endsWith('.tf') ? 'primary.main' : 'text.primary',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            label="Commit Message"
            value={commitMessage}
            onChange={e => setCommitMessage(e.target.value)}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCommit}
          disabled={loading || success || !selectedInstance || !selectedProject || !filePath}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {success ? 'Done' : 'Commit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
